// Comment anchoring (SPEC §6 "Anchoring comments", approved 2026-11-04/05).
// GitHub line comments must target RIGHT-side lines inside ONE diff hunk (context lines
// count). Hunks come from GitHub's patch headers when present, else from a local diff.
import { diffLines } from 'diff';

/** RIGHT-side (head) line range of a hunk, inclusive, 1-based. */
export type Hunk = { start: number; end: number };

/** Parse `@@ -a,b +c,d @@` headers of a unified patch. Headers only; the body is ignored. */
export function hunksFromPatch(patch: string): Hunk[] {
  const out: Hunk[] = [];
  for (const m of patch.matchAll(/^@@ -\d+(?:,\d+)? \+(\d+)(?:,(\d+))? @@/gm)) {
    const start = Number(m[1]);
    const count = m[2] === undefined ? 1 : Number(m[2]);
    if (count > 0) out.push({ start, end: start + count - 1 });
  }
  return out;
}

/** Fallback when GitHub omits `patch`: hunks of the base→head line diff with 3 context lines. */
export function localHunks(base: string, head: string, context = 3): Hunk[] {
  // Changed head-line ranges, plus deletion points (a pure deletion still makes a hunk).
  const changes: [number, number][] = [];
  let line = 1;
  for (const part of diffLines(base, head)) {
    const n = part.count ?? 0;
    if (part.added) { changes.push([line, line + n - 1]); line += n; }
    else if (part.removed) changes.push([line, line - 1]); // zero-width at `line`
    else line += n;
  }
  const headLines = head === '' ? 0 : head.replace(/\n$/, '').split('\n').length;
  const out: Hunk[] = [];
  for (const [s, e] of changes) {
    const start = Math.max(1, s - context);
    const end = Math.min(headLines, Math.max(e, s - 1) + context);
    if (end < start) continue;
    const last = out.at(-1);
    if (last && start <= last.end + 1) last.end = Math.max(last.end, end);
    else out.push({ start, end });
  }
  return out;
}

export type Anchor =
  | {
      kind: 'line';
      /** RIGHT side, inclusive. `startLine` omitted when equal to `line`. */
      startLine: number;
      line: number;
      /** The anchor covers the whole unit: "Suggest a change" is offered. */
      wholeUnit: boolean;
    }
  | { kind: 'file'; startLine: number; endLine: number };

/**
 * Anchor for a unit spanning head lines [unitStart, unitEnd]:
 * unit ∩ hunk. If several hunks intersect, use the one containing the unit's last changed
 * line (or the last intersecting hunk). No hunk → file-level comment.
 */
export function anchorFor(unitStart: number, unitEnd: number, hunks: Hunk[], changedLines?: Set<number>): Anchor {
  const hits = hunks.filter((h) => h.start <= unitEnd && h.end >= unitStart);
  if (!hits.length) return { kind: 'file', startLine: unitStart, endLine: unitEnd };
  let chosen = hits.at(-1)!;
  if (changedLines) {
    let lastChanged = -1;
    for (let l = unitStart; l <= unitEnd; l++) if (changedLines.has(l)) lastChanged = l;
    const h = hits.find((x) => x.start <= lastChanged && x.end >= lastChanged);
    if (h) chosen = h;
  }
  const startLine = Math.max(unitStart, chosen.start);
  const line = Math.min(unitEnd, chosen.end);
  return { kind: 'line', startLine, line, wholeUnit: startLine === unitStart && line === unitEnd };
}

/** Head lines added or modified (for "the hunk with the unit's last changed line"). */
export function changedHeadLines(base: string, head: string): Set<number> {
  const out = new Set<number>();
  let line = 1;
  for (const part of diffLines(base, head)) {
    const n = part.count ?? 0;
    if (part.added) for (let i = 0; i < n; i++) out.add(line + i);
    if (!part.removed) line += n;
  }
  return out;
}
